import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, call } from './client'
import { ApiError } from './errors'
import type { HouseholdInput, HouseholdPatch, Locale, Me, ThemePreference } from './types'

export const keys = {
  me: ['me'] as const,
  providers: ['auth', 'providers'] as const,
  households: ['households'] as const,
  household: (id: string) => ['households', id] as const,
  members: (id: string) => ['households', id, 'members'] as const,
  invitations: (id: string) => ['households', id, 'invitations'] as const,
  transfer: (id: string) => ['households', id, 'ownership-transfer'] as const,
  incomingTransfers: ['me', 'ownership-transfers'] as const,
  invitationPreview: (token: string) => ['invitation-preview', token] as const,
}

/** Current user; `null` when anonymous (401), error for anything else. */
export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: async (): Promise<Me | null> => {
      try {
        return await call(api.GET('/me'))
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null
        throw e
      }
    },
    staleTime: 60_000,
    retry: (count, error) => !(error instanceof ApiError) || (error.status >= 500 && count < 2),
  })
}

export function useAuthProviders() {
  return useQuery({
    queryKey: keys.providers,
    queryFn: () => call(api.GET('/auth/providers')),
    staleTime: 5 * 60_000,
    retry: false,
  })
}

/** Drops every user-scoped cache entry, then publishes the new identity (or null) to `useMe()` observers. */
export function resetUserCaches(queryClient: QueryClient, me: Me | null) {
  // The ['me'] query itself is kept (observers stay attached) and only has its data replaced.
  queryClient.removeQueries({
    predicate: (q) => q.queryKey[0] !== 'auth' && !(q.queryKey.length === 1 && q.queryKey[0] === 'me'),
  })
  queryClient.setQueryData(keys.me, me)
}

export function clearUserCaches(queryClient: QueryClient) {
  resetUserCaches(queryClient, null)
}

export function useHouseholds() {
  return useQuery({
    queryKey: keys.households,
    queryFn: () => call(api.GET('/households', { params: { query: { per_page: 100 } } })),
  })
}

export function useHousehold(id: string) {
  return useQuery({
    queryKey: keys.household(id),
    queryFn: () => call(api.GET('/households/{householdId}', { params: { path: { householdId: id } } })),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  })
}

export function useMembers(id: string) {
  return useQuery({
    queryKey: keys.members(id),
    queryFn: async () => (await call(api.GET('/households/{householdId}/members', { params: { path: { householdId: id } } }))).data,
  })
}

export function useInvitations(id: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.invitations(id),
    enabled,
    queryFn: async () =>
      (await call(api.GET('/households/{householdId}/invitations', { params: { path: { householdId: id } } }))).data,
  })
}

export function useHouseholdTransfer(id: string) {
  return useQuery({
    queryKey: keys.transfer(id),
    queryFn: async () => {
      try {
        return await call(api.GET('/households/{householdId}/ownership-transfer', { params: { path: { householdId: id } } }))
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
  })
}

export function useIncomingTransfers() {
  return useQuery({
    queryKey: keys.incomingTransfers,
    queryFn: async () => (await call(api.GET('/me/ownership-transfers'))).data,
  })
}

export function useInvitationPreview(token: string) {
  return useQuery({
    queryKey: keys.invitationPreview(token),
    queryFn: () => call(api.GET('/household-invitations/{token}', { params: { path: { token } } })),
    retry: false,
  })
}

/* ---------- mutations ---------- */

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; password: string; remember?: boolean }) => call(api.POST('/auth/login', { body })),
    onSuccess: (me) => resetUserCaches(qc, me),
  })
}

export function useRegister() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; email: string; password: string; password_confirmation: string; locale?: Locale }) =>
      call(api.POST('/auth/register', { body })),
    onSuccess: (me) => resetUserCaches(qc, me),
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      try {
        await call(api.POST('/auth/logout'))
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 401)) throw e
      }
    },
    onSuccess: () => clearUserCaches(qc),
  })
}

export function useUpdateMe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name?: string; locale?: Locale; theme?: ThemePreference }) => call(api.PATCH('/me', { body })),
    onSuccess: (me) => qc.setQueryData(keys.me, me),
  })
}

export function useCreateHousehold() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: HouseholdInput) => call(api.POST('/households', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.households }),
  })
}

export function useUpdateHousehold(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: HouseholdPatch & { version: number }) =>
      call(api.PATCH('/households/{householdId}', { params: { path: { householdId: id } }, body })),
    onSuccess: (household) => {
      qc.setQueryData(keys.household(id), household)
      void qc.invalidateQueries({ queryKey: keys.households })
    },
  })
}

export function useInviteMember(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { email: string; locale?: Locale }) =>
      call(api.POST('/households/{householdId}/invitations', { params: { path: { householdId: id } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invitations(id) }),
  })
}

export function useRevokeInvitation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      call(api.DELETE('/households/{householdId}/invitations/{invitationId}', { params: { path: { householdId: id, invitationId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invitations(id) }),
  })
}

export function useRemoveMember(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) =>
      call(api.DELETE('/households/{householdId}/members/{memberId}', { params: { path: { householdId: id, memberId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.members(id) })
      void qc.invalidateQueries({ queryKey: keys.household(id) })
    },
  })
}

export function useStartTransfer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) =>
      call(api.POST('/households/{householdId}/ownership-transfer', { params: { path: { householdId: id } }, body: { member_id: memberId } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.transfer(id) }),
  })
}

export function useCancelTransfer(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => call(api.DELETE('/households/{householdId}/ownership-transfer', { params: { path: { householdId: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.transfer(id) }),
  })
}

export function useAnswerTransfer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ transferId, accept }: { transferId: string; accept: boolean }) => {
      const params = { params: { path: { transferId } } }
      if (accept) await call(api.POST('/ownership-transfers/{transferId}/accept', params))
      else await call(api.POST('/ownership-transfers/{transferId}/decline', params))
    },
    onSuccess: () => qc.invalidateQueries(),
  })
}

export function useAcceptInvitation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => call(api.POST('/household-invitations/{token}/accept', { params: { path: { token } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.households }),
  })
}

export function useResendVerification() {
  return useMutation({ mutationFn: () => call(api.POST('/auth/email/resend')) })
}

export function useChangePassword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { current_password?: string; password: string; password_confirmation: string }) =>
      call(api.PUT('/me/password', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  })
}

export function useRequestEmailChange() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { new_email: string; current_password: string }) => call(api.POST('/me/email-change', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  })
}

export function useConfirmEmailChange() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => call(api.POST('/me/email-change/confirm', { body: { token } })),
    onSuccess: (me) => qc.setQueryData(keys.me, me),
  })
}

export function useUnlinkGoogle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => call(api.DELETE('/me/oauth-identities/{provider}', { params: { path: { provider: 'google' } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  })
}
